import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface SplashScreenProps {
  onComplete: () => void;
  message?: string; // 커스텀 메시지 (선택적)
  duration?: number; // 표시 시간 (밀리초, 기본값: 2000)
  showImmediately?: boolean; // 즉시 표시 여부 (기본값: false)
}

const SplashScreen: React.FC<SplashScreenProps> = ({ 
  onComplete, 
  message, 
  duration = 2000,
  showImmediately = false 
}) => {
  const [hasShown, setHasShown] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // showImmediately가 true면 무조건 표시 (알림 설정 등에서 사용)
    if (showImmediately) {
      setHasShown(true);
      const timer = setTimeout(() => {
        handleComplete();
      }, duration);
      return () => clearTimeout(timer);
    }

    // 기본 동작: 모바일이 아닌 경우 바로 완료
    if (!isMobile) {
      onComplete();
      return;
    }

    // 모바일에서 항상 스플래시 표시
    setHasShown(true);

    // 지정된 시간 후 자동 완료
    const timer = setTimeout(() => {
      handleComplete();
    }, duration);

    return () => clearTimeout(timer);
  }, [onComplete, isMobile, duration, showImmediately]);

  const handleComplete = () => {
    onComplete();
  };

  if (!hasShown) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        overflow: 'hidden'
      }}
    >
      {/* 로고 */}
      <Box
        sx={{
          width: isMobile ? 120 : 180,
          height: isMobile ? 120 : 180,
          mb: message ? 4 : (isMobile ? 3 : 4),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderRadius: '50%',
          background: '#1a2332',
          boxShadow: '0 12px 32px rgba(25,35,45,0.14)',
          animation: 'pulse-glow 1s cubic-bezier(0.23, 1, 0.32, 1) infinite'
        }}
      >
        <img
          src={`${window.location.origin}/Icon.png`}
          alt="JUDAM Logo"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
          }}
          onLoad={() => {
            console.log('Logo loaded successfully');
            console.log('Logo src:', `${window.location.origin}/Icon.png`);
          }}
          onError={(e) => {
            console.log('Logo failed to load');
            console.log('Attempted URL:', `${window.location.origin}/Icon.png`);
            console.log('Error details:', e);
            // 로고 로딩 실패 시 텍스트로 대체
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            // 부모 요소에 대체 텍스트 표시
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = '<div style="font-size: 64px; font-weight: bold; color: white;">🍽️</div>';
            }
          }}
        />
      </Box>

      {/* 커스텀 메시지 (선택적) - 한글자씩 통통 튀는 애니메이션 */}
      {message && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.5,
            px: 3
          }}
        >
          {message.split('').map((char, index) => {
            const animationDuration = 0.5; // 페이드인/아웃 애니메이션 시간
            const delayBetweenChars = 0.3; // 글자당 0.3초 간격
            
            // 공백이 아닌 글자의 순서 계산 (공백 제외)
            const nonSpaceIndex = message.slice(0, index).replace(/ /g, '').length;
            
            return (
              <Typography
                key={index}
                variant="h6"
                component="span"
                sx={{
                  color: '#1a2332',
                  fontWeight: 500,
                  display: 'inline-block',
                  // 공백은 애니메이션 없음, 글자만 순차적으로 페이드인/아웃
                  animation: char !== ' ' 
                    ? `fadeInOut ${animationDuration.toFixed(2)}s cubic-bezier(0.23, 1, 0.32, 1) ${(nonSpaceIndex * delayBetweenChars).toFixed(2)}s infinite`
                    : 'none',
                  textAlign: 'center',
                  opacity: char !== ' ' ? 0 : 1, // 글자는 초기에 투명, 공백은 항상 보임
                }}
              >
                {char === ' ' ? '\u00A0' : char}
              </Typography>
            );
          })}
        </Box>
      )}


      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes spin {
            0% { transform: translateX(-50%) rotate(0deg); }
            100% { transform: translateX(-50%) rotate(360deg); }
          }
          
          @keyframes pulse-glow {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 8px 25px rgba(255, 255, 255, 0.4);
            }
            50% {
              transform: scale(1.08);
              box-shadow: 0 12px 40px rgba(255, 255, 255, 0.6), 0 0 30px rgba(255, 255, 255, 0.4);
            }
          }
          
          @keyframes bounceInUp {
            0% {
              transform: translateY(0);
            }
            20% {
              transform: translateY(-15px);
            }
            40% {
              transform: translateY(5px);
            }
            60% {
              transform: translateY(0);
            }
            100% {
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInOut {
            0% {
              opacity: 0;
            }
            20% {
              opacity: 1;
            }
            80% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default SplashScreen; 